import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Animated,
  Platform,
  Modal,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Svg, Path, Circle } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useStartupPrompt } from '@/contexts/StartupPromptContext';
import PrimaryButton from './PrimaryButton';
import TertiaryButton from './TertiaryButton';

export default function UsernamePromptSheet() {
  const { t } = useLanguage();
  const { userProfile } = useAuth();
  const { currentPrompt, advanceQueue, backfilledUsername } = useStartupPrompt();
  const router = useRouter();

  const slideAnim = useRef(new Animated.Value(400)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  const visible = currentPrompt === 'username';
  const username = userProfile?.username || backfilledUsername || '';

  useEffect(() => {
    if (visible) {
      slideAnim.setValue(400);
      fadeAnim.setValue(0);

      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.spring(slideAnim, {
          toValue: 0,
          damping: 20,
          stiffness: 300,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible]);

  const handleGotIt = () => {
    advanceQueue();
  };

  const handleCustomize = () => {
    advanceQueue();
    router.push('/change-username');
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      statusBarTranslucent
      onRequestClose={() => {}}
    >
      <View style={styles.overlay}>
        <Animated.View style={[styles.backdrop, { opacity: fadeAnim }]} />
        <Animated.View
          style={[
            styles.sheet,
            { transform: [{ translateY: slideAnim }] },
          ]}
        >
          <View style={styles.handle} />

          <View style={styles.iconContainer}>
            <Svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <Circle cx="12" cy="8" r="4" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <Path
                d="M4 20C4 17.7909 7.58172 16 12 16C16.4183 16 20 17.7909 20 20"
                stroke="#015480"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
          </View>

          <Text style={styles.title}>{t('usernamePrompt.title')}</Text>
          <Text style={styles.description}>{t('usernamePrompt.description')}</Text>

          <View style={styles.handleBadge}>
            <Text style={styles.handleText}>@{username}</Text>
          </View>

          <View style={styles.buttonSection}>
            <PrimaryButton
              title={t('usernamePrompt.gotIt')}
              onPress={handleGotIt}
            />
          </View>

          <View style={styles.buttonGap} />

          <TertiaryButton
            title={t('usernamePrompt.customize')}
            onPress={handleCustomize}
          />
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(21, 21, 21, 0.5)',
  },
  sheet: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: 24,
    paddingBottom: Platform.OS === 'ios' ? 40 : 32,
    paddingTop: 12,
    alignItems: 'center',
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#DAD8CF',
    marginBottom: 24,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontFamily: 'Sentient-Regular',
    fontSize: 22,
    lineHeight: 28,
    color: '#151515',
    textAlign: 'center',
    marginBottom: 10,
  },
  description: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: '#7E7D75',
    textAlign: 'center',
    paddingHorizontal: 8,
    marginBottom: 20,
  },
  handleBadge: {
    backgroundColor: '#F0EEE4',
    borderRadius: 12,
    paddingHorizontal: 20,
    paddingVertical: 10,
    marginBottom: 8,
  },
  handleText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: '#015480',
  },
  buttonSection: {
    width: '100%',
    marginTop: 20,
  },
  buttonGap: {
    height: 8,
  },
});
